import { Piece, SocialPlacementChannel } from '@/app/common/general-types/briefs.dto';
import { productPriceLabel } from '../../drawers/a-product/a-product.helpers';
import { BRIEF_TYPE_CARD_TITLE } from '../conversation/smart-brief-thread';
import { liveBriefValue, SmartBriefDraft } from '../conversation/smart-brief-conversation';

export type LiveBriefBadge = 'you' | 'brief';

export type LiveBriefCard = {
  id: string;
  title: string;
  badge: LiveBriefBadge;
  lines: string[];
  kind: 'text' | 'pieces' | 'product';
  meta: string;
  productName: string;
  productDescription: string;
  productPrice: string;
  productImage: string;
};

export type LiveBriefSection = {
  id: 'setup' | 'read' | 'campaign';
  title: string;
  cards: LiveBriefCard[];
};

const CHANNEL_LABEL: Record<SocialPlacementChannel, string> = {
  tiktok_post: 'TikTok Post',
  instagram_reel: 'Instagram Reel',
  instagram_story: 'Instagram Story',
  youtube_shorts: 'YouTube Shorts',
  snapchat_spotlight: 'Snapchat Spotlight',
  snapchat_story: 'Snapchat Story',
};

export function liveBriefSections(draft: SmartBriefDraft): LiveBriefSection[] {
  return [
    { id: 'setup', title: 'Setup', cards: setupCards(draft) },
    { id: 'read', title: 'Read from your brief', cards: readCards(draft) },
    { id: 'campaign', title: 'Campaign details', cards: campaignCards(draft) },
  ];
}

export function briefLines(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }
  const text = trimmed
    .replace(/<li[^>]*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return text.split('\n').map((line) => line.trim()).filter((line) => line.length > 0);
}

function setupCards(draft: SmartBriefDraft): LiveBriefCard[] {
  const type = draft.briefType ? BRIEF_TYPE_CARD_TITLE[draft.briefType] : '';
  return [
    card('brief-type', 'Brief type', 'you', type ? [type] : []),
    card('source', 'Brief source', 'you', draft.source ? [draft.source] : []),
  ];
}

function readCards(draft: SmartBriefDraft): LiveBriefCard[] {
  const requirements = draft.briefType === 'ugc'
    ? [...briefLines(draft.steps), ...briefLines(draft.dos)]
    : briefLines(draft.requirements);
  const restrictions = draft.briefType === 'ugc'
    ? briefLines(draft.donts)
    : briefLines(draft.restrictions);
  const cards = [
    card('title', 'Brief title', 'brief', draft.title ? [draft.title] : []),
  ];
  if (draft.briefType === 'ugc') {
    cards.push(pieceCard(draft.pieces));
  }
  cards.push(
    card('requirements', 'Content requirements', 'brief', requirements),
    card('restrictions', 'Content restrictions', 'brief', restrictions),
    productCard(draft),
  );
  return cards;
}

function campaignCards(draft: SmartBriefDraft): LiveBriefCard[] {
  if (draft.briefType === 'screening') {
    return [
      card('lookback', 'Lookback', 'you', linesOrEmpty(liveBriefValue(draft, 'lookback'))),
      card('report', 'Report', 'you', linesOrEmpty(liveBriefValue(draft, 'report'))),
    ];
  }
  if (draft.briefType === 'monitoring') {
    return [
      card('start', 'Start', 'you', linesOrEmpty(liveBriefValue(draft, 'start'))),
      card('end', 'End', 'you', linesOrEmpty(liveBriefValue(draft, 'end'))),
    ];
  }
  const creators = draft.creatorCount === null ? [] : [`${draft.creatorCount} creators`];
  const audience = liveBriefValue(draft, 'audience');
  return [
    card('content-type', 'Content type', 'you', linesOrEmpty(contentType(draft.pieces))),
    card('creators', 'Creators needed', 'you', creators),
    card('audience', 'Creator requirements', 'you', linesOrEmpty(audience)),
  ];
}

function pieceCard(pieces: Piece[]): LiveBriefCard {
  const lines = pieces.map((piece) => {
    const placements = piece.placements.map((placement) => placement.kind === 'ugc' ? 'UGC' : CHANNEL_LABEL[placement.channel]);
    return placements.length > 0 ? `${piece.label}: ${placements.join(', ')}` : piece.label;
  });
  const posts = pieces.reduce((sum, piece) => sum + piece.placements.filter((placement) => placement.kind === 'social').length, 0);
  const meta = pieces.length === 0
    ? ''
    : posts > 0
      ? `${pieces.length} pieces · ${posts} posts`
      : `${pieces.length} pieces`;
  return { ...card('pieces', 'Content deliverables', 'you', lines), kind: 'pieces', meta };
}

function productCard(draft: SmartBriefDraft): LiveBriefCard {
  return {
    ...card('product', 'Product', 'you', []),
    kind: 'product',
    productName: draft.productName ?? '',
    productDescription: draft.productDescription ?? '',
    productPrice: productPriceLabel(draft.productValue ?? undefined, draft.productCurrency ?? undefined),
    productImage: draft.productImage ?? '',
  };
}

function contentType(pieces: Piece[]): string {
  const labels: string[] = [];
  for (const piece of pieces) {
    for (const placement of piece.placements) {
      const label = placement.kind === 'ugc' ? 'UGC only' : CHANNEL_LABEL[placement.channel];
      if (!labels.includes(label)) {
        labels.push(label);
      }
    }
  }
  return labels.join(', ');
}

function linesOrEmpty(value: string): string[] {
  return value.trim().length > 0 ? [value] : [];
}

function card(id: string, title: string, badge: LiveBriefBadge, lines: string[]): LiveBriefCard {
  return {
    id,
    title,
    badge,
    lines,
    kind: 'text',
    meta: '',
    productName: '',
    productDescription: '',
    productPrice: '',
    productImage: '',
  };
}
